import { useState, useEffect } from "react";
import { API, mediaUrl } from "../api";

function fallbackCopy(text) {
  const el = document.createElement("textarea");
  el.value = text;
  el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
  document.body.appendChild(el);
  el.focus();
  el.select();
  try { document.execCommand("copy"); } catch (_) {}
  document.body.removeChild(el);
}

// Reusable media browser. Pass onPick(file) to use it as a file picker.
export default function MediaBrowser({ rootPath = "", onPick, filter }) {
  const [provider, setProvider] = useState("");     // "" = Local disk
  const [providers, setProviders] = useState([]);   // enabled cloud providers
  const [path, setPath] = useState(rootPath);
  const [data, setData] = useState({ folders: [], files: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    API.storageProviders()
      .then((list) => setProviders(list.filter((p) => p.enabled)))
      .catch(() => {});
  }, []);

  const load = (p, prov = provider) => {
    setErr("");
    API.mediaList(p, prov)
      .then((d) => { setData(d); setPath(d.path); })
      .catch((e) => { setErr(e.message); setData({ folders: [], files: [] }); });
  };
  useEffect(() => { load(rootPath, provider); /* eslint-disable-next-line */ }, [rootPath, provider]);

  const switchSource = (prov) => { setProvider(prov); setPath(""); };

  const up = () => {
    const parts = path.split("/").filter(Boolean);
    parts.pop();
    load(parts.join("/"));
  };

  const addFolder = async () => {
    const name = window.prompt("New folder name:");
    if (!name) return;
    try { await API.mediaFolder(path, name, provider); load(path); }
    catch (e) { setErr(e.message); }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try { for (const f of files) await API.mediaUpload(path, f, provider); load(path); }
    catch (e2) { setErr(e2.message); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p}"?`)) return;
    try { await API.mediaDelete(p, provider); load(path); }
    catch (e) { setErr(e.message); }
  };

  const crumbs = ["", ...path.split("/").filter(Boolean).reduce((a, c) => [...a, a.length ? `${a[a.length - 1]}/${c}` : c], [])];
  let files = data.files;
  if (filter === "image") files = files.filter((f) => f.is_image);
  if (filter === "zip") files = files.filter((f) => f.is_zip);

  const rootLabel = provider ? (providers.find((p) => p.provider === provider)?.label || provider) : "media";

  return (
    <div className="mb" data-testid="media-browser">
      <div className="mb-sources" data-testid="mb-sources">
        <button className={`chip ${provider === "" ? "active" : ""}`} onClick={() => switchSource("")} data-testid="source-local">💾 Local</button>
        {providers.map((p) => (
          <button key={p.provider} className={`chip ${provider === p.provider ? "active" : ""}`}
            onClick={() => switchSource(p.provider)} data-testid={`source-${p.provider}`}>☁ {p.label}</button>
        ))}
      </div>
      <div className="mb-bar">
        <div className="mb-crumbs">
          {crumbs.map((c, i) => (
            <span key={c}>
              <button className="mb-crumb" onClick={() => load(c)}>{i === 0 ? rootLabel : c.split("/").pop()}</button>
              {i < crumbs.length - 1 && <span className="mb-sep">/</span>}
            </span>
          ))}
        </div>
        <div className="mb-actions">
          {path && <button className="btn ghost" onClick={up}>↑ Up</button>}
          <button className="btn ghost" onClick={addFolder} data-testid="mb-new-folder">+ Folder</button>
          <label className="btn primary" data-testid="mb-upload">
            {busy ? "Uploading…" : "Upload"}
            <input type="file" multiple hidden onChange={upload} />
          </label>
        </div>
      </div>
      {err && <div className="msg-error" data-testid="mb-error">{err}</div>}

      <div className="mb-grid">
        {data.folders.map((f) => (
          <div className="mb-item folder" key={f.path} onDoubleClick={() => load(f.path)} title="Double-click to open">
            <div className="mb-ico">📁</div>
            <div className="mb-name">{f.name}</div>
            <button className="mb-x" onClick={() => del(f.path)}>✕</button>
          </div>
        ))}
        {files.map((f) => (
          <div className="mb-item" key={f.path}>
            <div className="mb-ico">
              {f.is_image ? <img src={mediaUrl(f.url)} alt={f.name} /> : <span className="mb-file">{f.is_zip ? "🗜️" : "📄"}</span>}
            </div>
            <div className="mb-name" title={f.name}>{f.name}</div>
            <div className="mb-row">
              {onPick && <button className="mb-pick" onClick={() => onPick(f)} data-testid={`pick-${f.name}`}>Select</button>}
              <button className="mb-copy" onClick={() => {
                const url = mediaUrl(f.public_url || f.url);
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(url).catch(() => fallbackCopy(url));
                } else {
                  fallbackCopy(url);
                }
              }}>Copy URL</button>
              <button className="mb-x" onClick={() => del(f.path)}>✕</button>
            </div>
          </div>
        ))}
        {data.folders.length === 0 && files.length === 0 && <div className="empty">Empty folder. Upload files or create a folder.</div>}
      </div>
    </div>
  );
}
